import CurrencyBadgeUi from 'common/components/entities/OfferPriceNew/ui/CurrencyBadgeUi'
import React from 'react'
import {
  LineItemContainer,
  LineItemCurrencyContainer,
  LineItemLabel,
  LineItemPriceContainer,
  LineItemTitleContainer,
  LineSkeleton,
} from 'src/shared/components/offer-price/offer-line-item/ui'
import { RadioButton } from 'src/shared/components/radio-button/radio-button'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CalculatedTax } from 'src/shared/types/pricing-types'
import {
  centsToEuro,
  getReadablePrice,
} from 'src/shared/utils/price-calculator'
import { PriceFormatter } from 'src/shared/utils/price-plan-utils'

export type OfferLineNameProps = {
  nameFontFamily?: string
  mobileNameFontFamily?: string
  nameFontSize: number
  mobileNameFontSize?: number
  nameFontStyle?: string
  mobileNameFontStyle?: string
  nameFontWeight?: string
  mobileNameFontWeight?: string
  nameLineHeight: number
  mobileNameLineHeight?: number
  nameColor: string
  mobileNameColor?: string
}

export type OfferLineAmountProps = {
  amountFontFamily?: string
  mobileAmountFontFamily?: string
  amountFontSize: number
  mobileAmountFontSize?: number
  amountFontStyle?: string
  mobileAmountFontStyle?: string
  amountFontWeight?: string
  mobileAmountFontWeight?: string
  amountLineHeight: number
  mobileAmountLineHeight?: number
  amountColor: string
  mobileAmountColor?: string
}

export type OfferLineDescriptionProps = {
  descriptionFontFamily?: string
  mobileDescriptionFontFamily?: string
  descriptionFontSize: number
  mobileDescriptionFontSize?: number
  descriptionFontStyle?: string
  mobileDescriptionFontStyle?: string
  descriptionFontWeight?: string
  mobileDescriptionFontWeight?: string
  descriptionLineHeight: number
  mobileDescriptionLineHeight?: number
  descriptionColor: string
  mobileDescriptionColor?: string
}

export type OfferLineUiProps = OfferLineNameProps &
  OfferLineAmountProps &
  OfferLineDescriptionProps

export type OfferLineDataTestProps = {
  dataTestIdName?: string
  dataTestIdPrice?: string
  dataTestIdCurrency?: string
  dataTestIdTax?: string
}

export interface OfferLineItemProps
  extends OfferLineNameProps,
    OfferLineAmountProps,
    OfferLineDescriptionProps,
    OfferLineDataTestProps {
  label?: string
  amount?: number
  tax?: CalculatedTax
  priceFormatter?: PriceFormatter
  currency?: string
  locale: string
  isTaxInclusive?: boolean
  shouldDisplayCurrencyBadge?: boolean
  isSelected?: boolean
  onSelect?: () => void
  offerLineNameProps?: OfferLineNameProps
  offerLineAmountProps?: OfferLineAmountProps
}

export const OfferLineItem = ({
  label,
  amount,
  tax,
  priceFormatter,
  currency,
  locale,
  onSelect,
  isSelected = false,
  shouldDisplayCurrencyBadge = false,

  dataTestIdName,
  dataTestIdPrice,
  dataTestIdCurrency,
  dataTestIdTax,

  nameFontFamily,
  mobileNameFontFamily,
  nameFontSize,
  mobileNameFontSize,
  nameFontStyle,
  mobileNameFontStyle,
  nameFontWeight,
  mobileNameFontWeight,
  nameLineHeight,
  mobileNameLineHeight,
  nameColor,
  mobileNameColor,

  amountFontFamily,
  mobileAmountFontFamily,
  amountFontSize,
  mobileAmountFontSize,
  amountFontStyle,
  mobileAmountFontStyle,
  amountFontWeight,
  mobileAmountFontWeight,
  amountLineHeight,
  mobileAmountLineHeight,
  amountColor,
  mobileAmountColor,

  descriptionFontFamily,
  mobileDescriptionFontFamily,
  descriptionFontSize,
  mobileDescriptionFontSize,
  descriptionFontStyle,
  mobileDescriptionFontStyle,
  descriptionFontWeight,
  mobileDescriptionFontWeight,
  descriptionLineHeight,
  mobileDescriptionLineHeight,
  descriptionColor,
  mobileDescriptionColor,
}: OfferLineItemProps) => {
  const { t } = useTypedTranslation(['common'])
  const isSelectable = onSelect !== undefined

  if (!label || !currency || typeof amount === 'undefined') {
    return <LineSkeleton />
  }

  return (
    <LineItemContainer isPadded={!isSelectable}>
      <LineItemTitleContainer>
        {isSelectable && (
          <RadioButton
            name={label}
            checked={isSelected}
            onChange={onSelect}
            style={{ paddingInlineEnd: 28 }}
          />
        )}
        <LineItemLabel
          data-testid={dataTestIdName}
          fontFamily={nameFontFamily}
          mobileFontFamily={mobileNameFontFamily}
          fontSize={nameFontSize}
          mobileFontSize={mobileNameFontSize}
          fontStyle={nameFontStyle}
          mobileFontStyle={mobileNameFontStyle}
          fontWeight={nameFontWeight}
          mobileFontWeight={mobileNameFontWeight}
          lineHeight={nameLineHeight}
          mobileLineHeight={mobileNameLineHeight}
          color={nameColor}
          mobileColor={mobileNameColor}
        >
          {label}
        </LineItemLabel>
      </LineItemTitleContainer>
      <LineItemCurrencyContainer>
        {shouldDisplayCurrencyBadge && (
          <CurrencyBadgeUi data-testid={dataTestIdCurrency}>
            {currency}
          </CurrencyBadgeUi>
        )}
        <LineItemPriceContainer>
          <LineItemLabel
            data-testid={dataTestIdPrice}
            fontFamily={amountFontFamily}
            mobileFontFamily={mobileAmountFontFamily}
            fontSize={amountFontSize}
            mobileFontSize={mobileAmountFontSize}
            fontStyle={amountFontStyle}
            mobileFontStyle={mobileAmountFontStyle}
            fontWeight={amountFontWeight}
            mobileFontWeight={mobileAmountFontWeight}
            lineHeight={amountLineHeight}
            mobileLineHeight={mobileAmountLineHeight}
            color={amountColor}
            mobileColor={mobileAmountColor}
          >
            {priceFormatter
              ? priceFormatter(
                  getReadablePrice(centsToEuro(amount), currency, locale),
                )
              : getReadablePrice(centsToEuro(amount), currency, locale)}
          </LineItemLabel>
          {tax && tax.amount > 0 && (
            <LineItemLabel
              data-testid={dataTestIdTax}
              fontFamily={nameFontFamily}
              mobileFontFamily={mobileNameFontFamily}
              fontStyle={nameFontStyle}
              mobileFontStyle={mobileNameFontStyle}
              fontWeight={nameFontWeight}
              mobileFontWeight={mobileNameFontWeight}
              lineHeight={nameLineHeight}
              mobileLineHeight={mobileNameLineHeight}
              color={nameColor}
              mobileColor={mobileNameColor}
              style={{ fontSize: '75%', opacity: 0.8 }}
            >
              {t('components.offer_price.includes_vat_percentage', {
                percentage: tax.rate,
              })}
              {` (${getReadablePrice(centsToEuro(tax.amount), currency, locale)})`}
            </LineItemLabel>
          )}
        </LineItemPriceContainer>
      </LineItemCurrencyContainer>
    </LineItemContainer>
  )
}
